<template>
  <modal
    name="flight-manual-reservation-create-modal"
    :width="800"
    :height="'auto'"
    :scrollable="true"
    :adaptive="true"
    :clickToClose="false"
    class="modal-not-overflow"
    draggable=".popup__title"
    :reset="true"
    @before-close="onBeforeClose"
  >
    <div class="popup">
      <button @click="close" class="popup__close" type="button" aria-label="Close manual flight booking" :disabled="saving"></button>
      <div class="popup__title">Create Manual Flight Reservation</div>
      <div class="popup__body">
        <form @submit.prevent="createBooking" novalidate>
          <app-form-field v-if="dictionaryError" :margin-bottom="20">
            <template #error><div role="alert">{{ dictionaryError }}</div></template>
            <app-button @click.native="loadDictionaries" :disabled="dictionariesLoading" size="small">Retry</app-button>
          </app-form-field>
          <app-flex-grid
            :rows="[[1, 1], [1, 1, 1], ['150px', 1, 1], [1, 1, 'max-content'], [1]]"
            align-items="flex-end"
            margin-bottom="30px"
          >
            <template #header-1>
              <h3 class="title title--mb15">Flight Configuration</h3>
            </template>
            <template #col-1-1>
              <app-form-field label="Airline" required>
                <app-select
                  v-model="form.airline"
                  :options="airlines"
                  :clearable="false"
                  :loading="dictionariesLoading"
                  :disabled="dictionariesLoading || saving"
                  :error="$v.form.airline.$error || Boolean(fieldErrors.airline)"
                  @input="clearFieldError('airline')"
                  aria-label="Manual flight airline"
                  placeholder="Choose airline"
                />
                <template #error>
                  <div v-if="$v.form.airline.$dirty && !$v.form.airline.required">Required field</div>
                  <div v-else-if="fieldErrors.airline">{{ fieldErrors.airline }}</div>
                </template>
              </app-form-field>
            </template>
            <template #col-1-2>
              <app-form-field label="Action" required>
                <app-select
                  v-model="form.initial_action"
                  :options="actions"
                  :reduce="item => item.code"
                  :clearable="false"
                  :disabled="saving"
                  :error="$v.form.initial_action.$error || Boolean(fieldErrors.initial_action)"
                  @input="clearFieldError('initial_action')"
                  aria-label="Manual flight action"
                  placeholder="Choose action"
                />
                <template #error>
                  <div v-if="$v.form.initial_action.$dirty && !$v.form.initial_action.required">Required field</div>
                  <div v-else-if="fieldErrors.initial_action">{{ fieldErrors.initial_action }}</div>
                </template>
              </app-form-field>
            </template>
            <template #col-2-1>
              <app-form-field label="Adults" label-for="manual_flight_adults" required>
                <app-input
                  v-model.number="form.adults"
                  type="number"
                  id="manual_flight_adults"
                  :disabled="saving"
                  :error="$v.form.adults.$error || Boolean(fieldErrors.adults)"
                  @input="clearFieldError('adults')"
                />
                <template #error>
                  <div v-if="$v.form.adults.$dirty && !$v.form.adults.required">Required field</div>
                  <div v-else-if="$v.form.adults.$dirty && (!$v.form.adults.integer || !$v.form.adults.minValue)">Enter a whole number greater than zero</div>
                  <div v-else-if="$v.form.adults.$error">Enter a smaller number of adults</div>
                  <div v-else-if="fieldErrors.adults">{{ fieldErrors.adults }}</div>
                </template>
              </app-form-field>
            </template>
            <template #col-2-2>
              <app-form-field label="Children" label-for="manual_flight_children">
                <app-input
                  v-model.number="form.children" type="number" id="manual_flight_children" :disabled="saving"
                  :error="Boolean(fieldErrors.children)" @input="clearFieldError('children')"
                />
                <template #error><div v-if="fieldErrors.children">{{ fieldErrors.children }}</div></template>
              </app-form-field>
            </template>
            <template #col-2-3>
              <app-form-field label="Infants" label-for="manual_flight_infants">
                <app-input
                  v-model.number="form.infants" type="number" id="manual_flight_infants" :disabled="saving"
                  :error="Boolean(fieldErrors.infants)" @input="clearFieldError('infants')"
                />
                <template #error><div v-if="fieldErrors.infants">{{ fieldErrors.infants }}</div></template>
              </app-form-field>
            </template>
            <template #header-3>
              <h3 class="title title--mb15">Itinerary<span v-if="editingFlightIndex !== null">: Flight {{ editingFlightIndex + 1 }}</span></h3>
            </template>
            <template #col-3-1>
              <app-form-field label="Date" required>
                <app-date-picker
                  v-model="segment.date" placeholder="Date" :disabled="saving"
                  :disabled-date="disablePastDates"
                  :error="Boolean(segmentError('date'))" @input="onSegmentInput('date')"
                />
                <template #error><div v-if="segmentError('date')">{{ segmentError('date') }}</div></template>
              </app-form-field>
            </template>
            <template #col-3-2>
              <app-form-field label="Origin" required>
                <app-select
                  v-model="segment.origin"
                  :options="aerodromes"
                  :clearable="false"
                  :loading="dictionariesLoading"
                  :disabled="dictionariesLoading || saving"
                  :error="Boolean(segmentError('origin'))"
                  @input="onSegmentInput('origin')"
                  aria-label="Manual flight origin"
                  placeholder="Choose origin"
                />
                <template #error><div v-if="segmentError('origin')">{{ segmentError('origin') }}</div></template>
              </app-form-field>
            </template>
            <template #col-3-3>
              <app-form-field label="Destination" required>
                <app-select
                  v-model="segment.destination"
                  :options="aerodromes"
                  :clearable="false"
                  :loading="dictionariesLoading"
                  :disabled="dictionariesLoading || saving"
                  :error="Boolean(segmentError('destination'))"
                  @input="onSegmentInput('destination')"
                  aria-label="Manual flight destination"
                  placeholder="Choose destination"
                />
                <template #error><div v-if="segmentError('destination')">{{ segmentError('destination') }}</div></template>
              </app-form-field>
            </template>
            <template #col-4-1>
              <app-form-field label="Departure" label-for="manual_flight_departure" required>
                <app-input
                  v-model="segment.departure"
                  v-mask="'99:99'"
                  placeholder="00:00"
                  autocomplete="off"
                  id="manual_flight_departure"
                  :disabled="saving"
                  :error="Boolean(segmentError('departure'))"
                  @input="onSegmentInput('departure')"
                  @blur.native="$v.segment.departure.$touch()"
                  @paste.native.prevent
                />
                <template #error><div v-if="segmentError('departure')">{{ segmentError('departure') }}</div></template>
              </app-form-field>
            </template>
            <template #col-4-2>
              <app-form-field label="Arrival" label-for="manual_flight_arrival" required>
                <app-input
                  v-model="segment.arrival"
                  v-mask="'99:99'"
                  placeholder="00:00"
                  autocomplete="off"
                  id="manual_flight_arrival"
                  :disabled="saving"
                  :error="Boolean(segmentError('arrival'))"
                  @input="onSegmentInput('arrival')"
                  @blur.native="$v.segment.arrival.$touch()"
                  @paste.native.prevent
                />
                <template #error><div v-if="segmentError('arrival')">{{ segmentError('arrival') }}</div></template>
              </app-form-field>
            </template>
            <template #col-4-3>
              <app-button @click.native="addFlight" :disabled="saving || $v.segment.$invalid">Add</app-button>
            </template>
            <template #header-5>
              <h3 class="title title--mb15">Flights</h3>
            </template>
            <template #col-5-1>
              <ol v-if="flights.length" class="manual-flight-segments" aria-label="Flight segments">
                <flight-segment-row
                  v-for="(flight, index) in flights" :key="index"
                  date-near-route
                  :date="flight.date" :origin="flight.origin" :destination="flight.destination"
                  :departure="flight.departure" :arrival="flight.arrival"
                >
                  <template #actions>
                    <button
                      @click="removeFlight(index)"
                      class="popup__close manual-flight-remove"
                      type="button"
                      :aria-label="`Remove flight ${index + 1}`"
                      :disabled="saving"
                    ></button>
                  </template>
                </flight-segment-row>
              </ol>
              <app-no-results v-else title="No options" text="There’s no flights yet" small />
              <app-form-field v-if="flightsError()">
                <template #error><div>{{ flightsError() }}</div></template>
              </app-form-field>
            </template>
          </app-flex-grid>
          <app-cells position="center" :indent="false">
            <app-button type="submit" ref="submit" :disabled="saving || dictionariesLoading || Boolean(dictionaryError) || editingFlightIndex !== null">
              Create
            </app-button>
          </app-cells>
        </form>
      </div>
    </div>
  </modal>
</template>

<script>
import { getFlightsAirlines, getFlightsAerodromes, postFlightsCreateDirectManualBooking } from '@/http'
import { helpers, required, integer, minValue, maxValue } from 'vuelidate/lib/validators'
import { format, isValid, parseISO, startOfDay } from 'date-fns'
import FlightSegmentRow from '@/components/flights/FlightSegmentRow.vue'

const timeRegex = helpers.regex('timeRegex', /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/)
const differentAirports = (value, segment) => !helpers.req(value) || !segment.origin || !segment.destination || segment.origin.id !== segment.destination.id

const emptySegment = () => ({ date: null, origin: null, destination: null, departure: null, arrival: null })
const emptyForm = () => ({ airline: null, adults: 1, children: 0, infants: 0, initial_action: 'provisional' })

export default {
  name: 'ManualFlightBookingModal',
  components: { FlightSegmentRow },
  props: {
    leadId: { type: Number, required: true },
    canCreate: { type: Boolean, default: false }
  },
  data() {
    return {
      form: emptyForm(),
      segment: emptySegment(),
      flights: [],
      airlines: [],
      aerodromes: [],
      actions: [{ code: 'provisional', name: 'Provisional' }, { code: 'book', name: 'Book' }],
      dictionariesLoading: false,
      dictionaryError: '',
      dictionaryRequestId: 0,
      saving: false,
      fieldErrors: {},
      editingFlightIndex: null,
      validationDate: new Date(),
      createdBookingId: null,
    }
  },
  validations() {
    const segmentRules = {
      date: {
        required,
        futureDate(value) {
          if (!helpers.req(value)) return true
          const date = parseISO(value)
          return isValid(date) && format(date, 'yyyy-MM-dd') === value && date > startOfDay(this.validationDate)
        }
      },
      origin: { required, differentAirports },
      destination: { required, differentAirports },
      departure: { required, timeRegex },
      arrival: { required, timeRegex }
    }
    return {
      form: {
        airline: { required },
        adults: { required, integer, minValue: minValue(1), maxValue: maxValue(32767) },
        initial_action: { required }
      },
      segment: segmentRules,
      flights: { required, $each: segmentRules }
    }
  },
  beforeDestroy() {
    this.dictionaryRequestId += 1
  },
  methods: {
    open() {
      if (!this.canCreate || this.saving) return
      this.form = emptyForm()
      this.segment = emptySegment()
      this.flights = []
      this.fieldErrors = {}
      this.editingFlightIndex = null
      this.createdBookingId = null
      this.validationDate = new Date()
      this.$v.$reset()
      this.$modal.show('flight-manual-reservation-create-modal')
      this.loadDictionaries()
    },
    close() {
      if (!this.saving) this.$modal.hide('flight-manual-reservation-create-modal')
    },
    onBeforeClose(event) {
      if (this.saving) event.cancel()
    },
    async loadDictionaries() {
      const requestId = ++this.dictionaryRequestId
      this.dictionariesLoading = true
      this.dictionaryError = ''
      this.airlines = []
      this.aerodromes = []
      try {
        const [airlines, aerodromes] = await Promise.all([getFlightsAirlines(), getFlightsAerodromes()])
        if (requestId !== this.dictionaryRequestId) return
        this.airlines = Array.isArray(airlines.data) ? airlines.data : []
        this.aerodromes = Array.isArray(aerodromes.data) ? aerodromes.data : []
        if (!this.airlines.length || !this.aerodromes.length) this.dictionaryError = 'No airlines or aerodromes available.'
      } catch (error) {
        if (requestId !== this.dictionaryRequestId) return
        this.dictionaryError = error.response?.data?.details || error.response?.data?.detail || 'Unable to load airlines and aerodromes. Please retry.'
      } finally {
        if (requestId === this.dictionaryRequestId) this.dictionariesLoading = false
      }
    },
    addFlight() {
      if (this.saving) return
      this.validationDate = new Date()
      this.$v.segment.$touch()
      if (this.$v.segment.$invalid) return
      if (this.editingFlightIndex === null) this.flights.push({ ...this.segment })
      else {
        this.$set(this.flights, this.editingFlightIndex, { ...this.segment })
        Object.keys(this.fieldErrors).forEach(path => {
          if (path === `flights.${this.editingFlightIndex}` || path.startsWith(`flights.${this.editingFlightIndex}.`)) this.clearFieldError(path)
        })
      }
      this.segment = emptySegment()
      this.$v.segment.$reset()
      this.editingFlightIndex = null
      this.clearFieldError('flights')
      this.restoreRejectedFlight()
    },
    removeFlight(index) {
      if (this.saving) return
      this.flights.splice(index, 1)
      if (this.editingFlightIndex === index) {
        this.editingFlightIndex = null
        this.segment = emptySegment()
        this.$v.segment.$reset()
      } else if (this.editingFlightIndex !== null && this.editingFlightIndex > index) this.editingFlightIndex -= 1
      const shiftedErrors = {}
      Object.entries(this.fieldErrors).forEach(([path, message]) => {
        const match = /^flights\.(\d+)(.*)$/.exec(path)
        if (!match) {
          if (path !== 'flights') shiftedErrors[path] = message
        } else if (Number(match[1]) !== index) {
          const nextIndex = Number(match[1]) > index ? Number(match[1]) - 1 : Number(match[1])
          shiftedErrors[`flights.${nextIndex}${match[2]}`] = message
        }
      })
      this.fieldErrors = shiftedErrors
      if (this.editingFlightIndex === null) this.restoreRejectedFlight()
    },
    clearFieldError(field) {
      this.$delete(this.fieldErrors, field)
    },
    flightsError() {
      return this.$v.flights.$dirty && !this.$v.flights.required ? 'Add at least one flight' : this.fieldErrors.flights || ''
    },
    segmentError(field) {
      const validation = this.$v.segment[field]
      if (validation.$dirty) {
        if (!validation.required) return 'Required field'
        if (field === 'date' && !validation.futureDate) return 'Choose tomorrow or a later date'
        if (['origin', 'destination'].includes(field) && !validation.differentAirports) return 'Origin and destination must be different'
        if (['departure', 'arrival'].includes(field) && !validation.timeRegex) return 'Enter a valid time in 24-hour format (HH:mm)'
      }
      if (this.editingFlightIndex === null) return ''
      const direct = this.fieldErrors[`flights.${this.editingFlightIndex}.${field}`]
      const route = ['origin', 'destination'].includes(field) ? this.fieldErrors[`flights.${this.editingFlightIndex}`] : ''
      return direct || route || ''
    },
    disablePastDates(date) {
      return startOfDay(date) <= startOfDay(new Date())
    },
    onSegmentInput(field) {
      this.validationDate = new Date()
      this.clearSegmentError(field)
      this.$v.segment[field].$touch()
      if (['origin', 'destination'].includes(field)) {
        const other = field === 'origin' ? 'destination' : 'origin'
        if (this.segment[other]) this.$v.segment[other].$touch()
      }
    },
    clearSegmentError(field) {
      if (this.editingFlightIndex === null) return
      this.clearFieldError(`flights.${this.editingFlightIndex}.${field}`)
      if (['origin', 'destination'].includes(field)) this.clearFieldError(`flights.${this.editingFlightIndex}`)
    },
    applyBookingError(error) {
      const data = error.response?.data || {}
      const details = data.details || data.detail || error.message || 'Unable to create booking. Please try again.'
      const fields = ['airline', 'initial_action', 'adults', 'children', 'infants', 'flights']
      const errors = {}
      const general = []
      // Flights API flattens serializer errors into "flights.0.origin: message; ...".
      String(details).split(/;\s+(?=(?:airline|initial_action|adults|children|infants|flights)(?:\.|:))/).forEach(message => {
        const match = /^(airline|initial_action|adults|children|infants|flights(?:\.\d+(?:\.(?:origin|destination|date|departure|arrival))?)?):\s*(.+)$/.exec(message)
        if (match) errors[match[1]] = [errors[match[1]], match[2]].filter(Boolean).join(' ')
        else if (message === 'Airline was not found.' || message.startsWith('Airline must have both child age boundaries configured ')) errors.airline = message
        else if (message === 'Infants cannot outnumber adults.') errors.infants = message
        else general.push(message)
      })
      // Other forms use a field-error map with AppFormField's error slot.
      const structured = data.errors || data
      fields.forEach(field => {
        const value = structured[field]
        if (typeof value === 'string' || (Array.isArray(value) && value.every(item => typeof item === 'string'))) {
          errors[field] = Array.isArray(value) ? value.join(' ') : value
        }
      })
      this.fieldErrors = errors
      this.restoreRejectedFlight()
      if (general.length && (!Object.keys(errors).length || data.details || data.detail)) {
        this.$notify({ type: 'error', title: general.join(' ') })
      }
    },
    restoreRejectedFlight() {
      const invalidIndices = Object.keys(this.fieldErrors).map(path => /^flights\.(\d+)/.exec(path))
        .filter(Boolean).map(match => Number(match[1])).filter(index => this.flights[index])
      if (invalidIndices.length) {
        this.editingFlightIndex = Math.min(...invalidIndices)
        this.segment = { ...this.flights[this.editingFlightIndex] }
        this.$v.segment.$touch()
      }
    },
    payload() {
      return {
        initial_action: this.form.initial_action,
        lead_id: this.leadId,
        airline: this.form.airline.id,
        adults: Number(this.form.adults),
        children: Number(this.form.children ?? 0),
        infants: Number(this.form.infants ?? 0),
        flights: this.flights.map(flight => ({
          origin: flight.origin ? flight.origin.id : null,
          destination: flight.destination ? flight.destination.id : null,
          date: flight.date,
          departure: flight.departure,
          arrival: flight.arrival,
        }))
      }
    },
    async createBooking() {
      if (!this.canCreate || this.saving || this.dictionariesLoading || this.dictionaryError || this.editingFlightIndex !== null) return
      this.validationDate = new Date()
      this.$v.form.$touch()
      if (this.$v.form.$invalid) return
      if (!this.createdBookingId) {
        this.$v.flights.$touch()
        if (!this.$v.flights.required) return
        const invalidIndex = this.flights.findIndex((flight, index) => this.$v.flights.$each[index].$invalid)
        if (invalidIndex !== -1) {
          this.editingFlightIndex = invalidIndex
          this.segment = { ...this.flights[invalidIndex] }
          this.$v.segment.$touch()
          return
        }
      }
      this.saving = true
      this.fieldErrors = {}
      this.$refs.submit.preload = true
      try {
        // Direct creation is not idempotent. A navigation retry must reuse the created booking.
        if (!this.createdBookingId) {
          const { data } = await postFlightsCreateDirectManualBooking(this.payload())
          if (!Number.isInteger(data?.id) || data.id <= 0) {
            throw new Error('The server did not return a booking ID. Check the booking list before retrying.')
          }
          this.createdBookingId = data.id
        }
        await this.$router.push({ name: 'flights-bookings-detail', params: { id: this.createdBookingId } })
        this.saving = false
        this.close()
      } catch (error) {
        this.applyBookingError(error)
      } finally {
        this.saving = false
        if (this.$refs.submit) this.$refs.submit.preload = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.manual-flight-segments { list-style: none; margin: 0; padding: 0; border-top: 1px solid #dce0d6; }
.manual-flight-remove { position: static; }
</style>
